import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, NavLink, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import Landing from './components/Landing';
import AboutUs from './components/AboutUs';
import DailyData from './components/DailyData';
import { Logo } from './components/Logo';
import DeveloperCredit from './components/DeveloperCredit';
import {
  Container,
  Segmented,
  SegmentedItem,
  IconMenu,
  IconClose,
  IconChevronRight,
  pressable,
} from './components/ui';
import './App.css';

// ------------------------------------------------------------------ header

const HeaderBar = styled.header`
  position: sticky;
  top: 0;
  z-index: 50;
  background: var(--material);
  backdrop-filter: var(--material-filter);
  -webkit-backdrop-filter: var(--material-filter);
  /* Scroll-edge effect: the hairline only appears once content passes underneath */
  box-shadow: 0 1px 0 ${(p) => (p.$scrolled ? 'var(--separator)' : 'transparent')};
  transition: box-shadow 200ms ease;
`;

const HeaderInner = styled(Container)`
  height: var(--header-h);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const Brand = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: var(--text);
  text-decoration: none;
  border-radius: 8px;
  ${pressable}
`;

const NavLinks = styled.nav`
  display: flex;
  align-items: center;
  gap: 4px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const HeaderLink = styled(NavLink)`
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 450;
  color: var(--text-secondary);
  text-decoration: none;
  ${pressable}

  &:hover {
    color: var(--text);
  }

  &.active {
    color: var(--text);
    font-weight: 550;
    background: var(--fill);
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DesktopOnly = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

const LanguageItem = styled(SegmentedItem)`
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
`;

const MenuButton = styled.button`
  display: none;
  width: 40px;
  height: 40px;
  margin-right: -8px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: var(--text);
  cursor: pointer;
  ${pressable}

  &:hover {
    background: var(--fill);
  }

  @media (max-width: 768px) {
    display: inline-grid;
    place-items: center;
  }
`;

// Scrim and sheet live outside the header: its backdrop-filter would otherwise
// become their containing block and backdrop root.
const Scrim = styled.div`
  position: fixed;
  inset: var(--header-h) 0 0 0;
  z-index: 45;
  background: var(--scrim);
  opacity: ${(p) => (p.$open ? 1 : 0)};
  pointer-events: ${(p) => (p.$open ? 'auto' : 'none')};
  transition: opacity 260ms ease;

  @media (min-width: 769px) {
    display: none;
  }
`;

// Anchored to the header it drops from, entering and leaving along the same path.
// Toggling a CSS transition keeps it interruptible: a mid-flight tap reverses from the current value.
const MobileSheet = styled.div`
  position: fixed;
  top: var(--header-h);
  left: 0;
  right: 0;
  z-index: 49;
  background: var(--material-strong);
  backdrop-filter: var(--material-filter);
  -webkit-backdrop-filter: var(--material-filter);
  border-bottom: 1px solid var(--separator);
  box-shadow: var(--shadow-md);
  padding: 4px var(--gutter) 20px;
  transform-origin: top center;
  opacity: ${(p) => (p.$open ? 1 : 0)};
  transform: translateY(${(p) => (p.$open ? '0' : '-12px')});
  visibility: ${(p) => (p.$open ? 'visible' : 'hidden')};
  transition:
    opacity 220ms ease,
    transform 340ms var(--ease-out),
    visibility 0s linear ${(p) => (p.$open ? '0s' : '340ms')};

  @media (min-width: 769px) {
    display: none;
  }

  @media (prefers-reduced-motion: reduce) {
    transform: none;
  }
`;

const MobileLink = styled(NavLink)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 0;
  border-bottom: 1px solid var(--separator);
  color: var(--text);
  text-decoration: none;
  font-size: 1.0625rem;
  font-weight: 500;
  letter-spacing: -0.015em;

  svg {
    color: var(--text-tertiary);
  }

  &.active {
    color: var(--accent);
  }
`;

const MobileLanguage = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 16px;
  color: var(--text-secondary);
  font-size: 0.9375rem;
`;

function LanguageSwitch({ language, onChange, label }) {
  return (
    <Segmented role="group" aria-label={label}>
      <LanguageItem $active={language === 'en'} aria-pressed={language === 'en'} aria-label="English" onClick={() => onChange('en')}>
        EN
      </LanguageItem>
      <LanguageItem $active={language === 'uk'} aria-pressed={language === 'uk'} aria-label="Українська" onClick={() => onChange('uk')}>
        UA
      </LanguageItem>
    </Segmented>
  );
}

function SiteHeader({ locale, language, onLanguageChange }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => e.key === 'Escape' && setMenuOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const links = [
    { to: '/', label: locale.dashboard, end: true },
    { to: '/daily-data', label: locale.dailyData },
    { to: '/about', label: locale.about },
  ];

  return (
    <>
      <HeaderBar $scrolled={scrolled || menuOpen}>
        <HeaderInner>
          <Brand to="/">
            <Logo height={28} title={locale.brandName} />
          </Brand>

          <HeaderActions>
            <NavLinks aria-label={locale.navLabel}>
              {links.map((link) => (
                <HeaderLink key={link.to} to={link.to} end={link.end}>
                  {link.label}
                </HeaderLink>
              ))}
            </NavLinks>
            <DesktopOnly>
              <LanguageSwitch language={language} onChange={onLanguageChange} label={locale.languageLabel} />
            </DesktopOnly>
            <MenuButton
              aria-label={menuOpen ? locale.closeMenu : locale.openMenu}
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
              onClick={() => setMenuOpen((v) => !v)}
            >
              {menuOpen ? <IconClose size={22} /> : <IconMenu size={22} />}
            </MenuButton>
          </HeaderActions>
        </HeaderInner>
      </HeaderBar>

      <Scrim $open={menuOpen} onClick={() => setMenuOpen(false)} aria-hidden="true" />
      <MobileSheet id="mobile-menu" $open={menuOpen} aria-hidden={!menuOpen}>
        <nav aria-label={locale.navLabel}>
          {links.map((link) => (
            <MobileLink key={link.to} to={link.to} end={link.end} tabIndex={menuOpen ? 0 : -1}>
              {link.label}
              <IconChevronRight size={18} />
            </MobileLink>
          ))}
        </nav>
        <MobileLanguage>
          <span>{locale.languageLabel}</span>
          <LanguageSwitch language={language} onChange={onLanguageChange} label={locale.languageLabel} />
        </MobileLanguage>
      </MobileSheet>
    </>
  );
}

// ------------------------------------------------------------------ footer

const FooterBar = styled.footer`
  margin-top: auto;
  padding: 48px 0 40px;
  border-top: 1px solid var(--separator);
  background: var(--surface-muted);
  color: var(--text-secondary);
  font-size: 0.8125rem;
  line-height: 1.55;
`;

const FooterTop = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) repeat(2, minmax(0, 1fr));
  gap: 32px;
  padding-bottom: 32px;
  border-bottom: 1px solid var(--separator);

  @media (max-width: 640px) {
    grid-template-columns: 1fr 1fr;

    > :first-child {
      grid-column: 1 / -1;
    }
  }
`;

const FooterBrand = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 320px;

`;

const FooterCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  h4 {
    color: var(--text);
    font-size: 0.8125rem;
    font-weight: 600;
    margin-bottom: 2px;
  }

  a {
    color: var(--text-secondary);
    text-decoration: none;
    width: fit-content;
    overflow-wrap: anywhere;

    &:hover {
      color: var(--text);
    }
  }
`;

const FooterBottom = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 8px 24px;
  padding-top: 20px;
  color: var(--text-tertiary);

  p:last-child {
    max-width: 560px;
  }
`;

function SiteFooter({ locale, header, email }) {
  const year = new Date().getFullYear();
  return (
    <FooterBar>
      <Container>
        <FooterTop>
          <FooterBrand>
            <Brand to="/">
              <Logo height={26} title={header.brandName} />
            </Brand>
            <p>{locale.tagline}</p>
          </FooterBrand>
          <FooterCol>
            <h4>{locale.product}</h4>
            <Link to="/">{header.dashboard}</Link>
            <Link to="/daily-data">{header.dailyData}</Link>
          </FooterCol>
          <FooterCol>
            <h4>{locale.company}</h4>
            <Link to="/about">{header.about}</Link>
            {email && <a href={`mailto:${email}`}>{email}</a>}
            <span>{locale.location}</span>
          </FooterCol>
        </FooterTop>
        <FooterBottom>
          <p>
            © {year} {locale.legalName}. {locale.rights}
          </p>
          <p>{locale.disclaimer}</p>
        </FooterBottom>
      </Container>
    </FooterBar>
  );
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    // 'instant' overrides the smooth scroll-behavior on <html>: a new page should simply start at the top.
    if (!hash) window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname, hash]);
  return null;
}

const routeIn = keyframes`
  from {
    opacity: 0;
    transform: translate3d(0, 12px, 0);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

// Each page settles in on navigation; keyed by path so it replays per route.
const RouteStage = styled.div`
  animation: ${routeIn} 600ms var(--ease-out) both;

  @media (prefers-reduced-motion: reduce) {
    animation-name: none;
  }
`;

function AnimatedRoutes({ locale }) {
  const location = useLocation();
  return (
    <RouteStage key={location.pathname}>
      <Routes location={location}>
        <Route path="/" element={<Landing locale={locale} />} />
        <Route path="/daily-data" element={<DailyData locale={locale.daily} />} />
        <Route path="/about" element={<AboutUs locale={locale.about} />} />
      </Routes>
    </RouteStage>
  );
}

const Main = styled.main`
  flex: 1;
  width: 100%;
  padding-bottom: clamp(56px, 8vw, 96px);
`;

// ------------------------------------------------------------------ copy

const translations = {
  en: {
    header: {
      dashboard: 'Dashboard',
      dailyData: 'Daily data',
      about: 'About',
      languageLabel: 'Language',
      brandName: 'FundingPulse',
      navLabel: 'Main navigation',
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
    },
    hero: {
      tag: 'Real-time analysis',
      title: 'Funding rate signals for futures traders',
      text:
        'Discover when exchanges pay long or short holders, track changes over time, and keep a unified view of the most relevant markets — in one clean workspace.',
      primaryAction: 'Launch dashboard',
      secondaryAction: 'About the company',
    },
    stats: [
      { value: '{exchanges}', label: 'Derivatives exchanges covered' },
      { value: '2', label: 'Contract types — USDT-M and COIN-M' },
      { value: '365', label: 'Days of history in a single query' },
      { value: 'Daily', label: 'Automated cross-exchange snapshot' },
    ],
    coverage: {
      label: 'Funding data from leading derivatives venues',
    },
    market: {
      eyebrow: 'Market now',
      title: 'Funding across exchanges',
      lead: 'Average funding rate on USDT-M perpetuals by exchange, from the latest daily snapshot.',
      asset: 'Asset',
      period: 'Period',
      periods: { '1d': '1D', '30d': '30D', '90d': '90D', '365d': '1Y' },
      periodLong: { '1d': 'last day', '30d': 'last 30 days', '90d': 'last 90 days', '365d': 'last 365 days' },
      highest: 'Highest',
      lowest: 'Lowest',
      spread: 'Cross-exchange spread',
      spreadNote: 'Highest minus lowest',
      average: 'Average',
      averageNote: 'Across {count} exchanges',
      snapshot: 'Snapshot {date} · USDT-M perpetuals · {period}',
      cta: 'Open daily data',
      records: 'Records',
      noData: 'No snapshot for this asset yet.',
    },
    featuresHeader: {
      eyebrow: 'Platform',
      title: 'Built for disciplined futures research',
    },
    features: [
      {
        title: 'Cross-exchange view',
        text: 'Compare funding rates side by side to spot arbitrage opportunities faster.',
      },
      {
        title: 'Flexible periods',
        text: 'Choose daily, weekly, or custom ranges to study funding dynamics that matter.',
      },
      {
        title: 'Actionable insight',
        text: 'Historical context helps you understand which way funding is likely to swing.',
      },
      {
        title: 'Low latency APIs',
        text: 'Reliable feeds from Tier 1 exchanges keep your models aligned with the latest ticks.',
      },
    ],
    workspace: {
      eyebrow: 'Dashboard',
      title: 'Analyze funding rate history',
      lead: 'Pick a period, an exchange and a pair — the controls stay pinned while you read the results.',
    },
    home: {
      periodTitle: 'Select a period',
      exchangeTitle: 'Select an exchange',
      contractTypeTitle: 'Contract type',
      tradingPairTitle: 'Select a trading pair',
      dock: {
        period: 'Period',
        exchange: 'Exchange',
        pair: 'Pair',
        all: 'All',
        hide: 'Hide',
        chooseExchangeFirst: 'Choose an exchange first',
        loadingPairs: 'Loading pairs…',
        noPairs: 'No pairs',
        emptyTitle: 'Choose a trading pair',
        emptyText: 'Statistics, the funding rate chart and the full history will appear here.',
      },
      resultTitle: 'Analysis result',
      contractType: {
        usdtMDesc: 'USDT-margined perpetual futures',
        coinMDesc: 'Coin-margined perpetual futures',
      },
      periodSelector: {
        dateLocale: 'en-US',
        startLabel: 'Start date',
        endLabel: 'End date',
        startPlaceholder: 'Select start date',
        endPlaceholder: 'Select end date',
        selectedRangeLabel: 'Selected period',
        periodTypes: [
          { id: 'custom', label: 'Custom range' },
          { id: 'today', label: 'Today' },
          { id: 'yesterday', label: 'Yesterday' },
          { id: 'week', label: '7 days' },
          { id: 'month', label: '30 days' },
          { id: 'year', label: '365 days' },
        ],
      },
      exchangeSelector: {
        searchPlaceholder: 'Search exchange',
        searchAria: 'Search exchange',
        statusActive: 'Active',
        statusInactive: 'Inactive',
        selectedTitle: 'Selected exchange:',
        selectedSubtitle: 'Ready to pick trading pairs',
        noResults: 'No exchanges match “{query}”',
      },
      tradingPairSelector: {
        searchPlaceholder: 'Search trading pair, e.g. BTC or USDT',
        searchAria: 'Search trading pair',
        loadingMessage: 'Loading trading pairs…',
        errorTitle: 'Error',
        errorPrefix: 'Loading error:',
        placeholderTitle: 'No trading pairs found',
        placeholderSupportedHint: 'Try changing the query or checking your connection.',
        placeholderUnsupportedHint: 'This exchange is not supported yet.',
        placeholderNoExchangeHint: 'Select an exchange to load trading pairs.',
        paginationPrev: 'Previous',
        paginationNext: 'Next',
        paginationInfo: 'Page {current} of {pages} · {total} pairs',
        rateLabel: 'Funding rate',
        noCoinM: '{exchange} has no COIN-M contracts. Switch to USDT-M to see available pairs.',
        spotOnly: '{exchange} is a spot exchange without perpetual futures, so funding rates are not available.',
      },
      fundingRateResult: {
        loadingMessage: 'Loading funding rate history…',
        noDataTitle: 'No data',
        noDataMessage: 'Unable to load funding rate data',
        analysisTitlePrefix: 'Funding rate analysis',
        availablePeriodLabel: 'Available data window',
        requestedRangeLabel: 'Requested range:',
        averageLabel: 'Average rate',
        totalLabel: 'Total sum',
        recordsLabel: 'Records',
        minLabel: 'Minimum rate',
        maxLabel: 'Maximum rate',
        chartTitle: 'Funding rate over time',
        detailsTitle: 'Detailed period data',
        dateColumn: 'Date & time',
        rateColumn: 'Funding rate',
        sortLabel: 'Sort',
        sortNewest: 'Newest first',
        sortOldest: 'Oldest first',
        dateLocale: 'en-US',
      },
    },
    daily: {
      eyebrow: 'Daily data',
      title: 'Daily funding snapshot',
      lead: 'Average, total, minimum and maximum funding rates across all covered exchanges for 1, 30, 90 and 365 days.',
      dateLocale: 'en-US',
      currency: 'Currency',
      date: 'Date',
      dateHint: 'The date is set automatically for the selected currency',
      reload: 'Reload',
      reloading: 'Loading…',
      fetchFresh: 'Fetch fresh data',
      saving: 'Saving…',
      availableDates: 'Available dates',
      moreDates: 'and {count} more',
      schedulerTitle: 'Daily update time (UTC)',
      schedulerLabel: 'Hour : minute',
      hour: 'Hour',
      minute: 'Minute',
      saveTime: 'Save time',
      refresh: 'Refresh',
      loading: 'Loading data…',
      totalExchanges: 'Exchanges in snapshot',
      snapshotTime: 'Snapshot date & time',
      tableTitle: 'Daily funding rates · {currency}',
      exchange: 'Exchange',
      contractType: 'Contract type',
      periods: ['1 day', '30 days', '90 days', '365 days'],
      yesterday: 'yesterday',
      metrics: { avg: 'Avg', sum: 'Sum', min: 'Min', max: 'Max' },
      records: '{count} records',
      chartsTitle: 'Visualization',
      emptyTitle: 'No data to display',
      emptyText: 'Choose a currency and press “Reload” to view the data.',
      loaded: 'Data for {date} ({currency}) loaded',
      notFound: 'No data for {date} ({currency}). Try another date or wait until 15:00.',
      loadError: 'Failed to load data. Please try again later.',
      savedOk: 'Data saved: {file}',
      saveFailed: 'Save failed: {message}',
      saveError: 'Failed to save data. Please try again later.',
      invalidTime: 'Invalid time (hour 0–23, minute 0–59)',
      passwordPrompt: 'Enter the password to confirm changes',
      cancelled: 'Cancelled: no password entered',
      updated: 'Updated: {time}',
      serverError: 'The server returned an error',
      schedulerError: 'Failed to set the scheduler time',
      charts: {
        periods: ['1 day', '30 days', '90 days', '365 days'],
        contractTypes: 'Contract type distribution',
        average: 'Average funding rates by period',
        max: 'Maximum funding rates',
        min: 'Minimum funding rates',
        total: 'Funding rate sums',
        axisRate: 'Funding rate (%)',
        axisMax: 'Maximum funding rate (%)',
        axisMin: 'Minimum funding rate (%)',
        axisTotal: 'Funding rate sum',
        axisExchanges: 'Exchanges',
        records: 'Records',
      },
    },
    about: {
      eyebrow: 'Company',
      title: 'About us',
      missionTitle: 'Our mission',
      missionText:
        'We build a user-friendly platform for analyzing funding rates across the most traded cryptocurrency exchanges. Our goal is to give traders and investors fast access to relevant funding data so they can make informed decisions.',
      fundingTitle: 'What is the funding rate?',
      fundingText:
        'The funding rate is the periodic payment exchanged between long and short position holders on perpetual futures markets. It reflects the gap between the perpetual contract and spot price — positive funding means longs pay shorts, negative turns the tables.',
      platformTitle: 'Platform highlights',
      platformList: [
        'Cross-exchange monitoring to catch arbitrage opportunities early.',
        'Flexible period selection for deep historical analysis.',
        'Rich analytics with supporting visuals and context.',
        'Smart alerts for meaningful funding shifts.',
        'Responsive interface tuned for quick insights.',
      ],
      supportedTitle: 'Supported exchanges',
      companyTitle: 'Company details',
      infoIntro:
        'Reach out with partnership ideas, press inquiries, or feedback — below is the fastest way to connect with us.',
      companyInfo: [
        { label: 'Company name', value: 'KUON GROUP LTD' },
        {
          label: 'Office address',
          value: 'Office 15718 182-184, High Street North, East Ham, London, United Kingdom, E6 2JA',
        },
        { label: 'Phone', value: '+44 73 5395 8683' },
        { label: 'Email', value: 'support@ratex.top' },
        { label: 'Office hours', value: 'Mon – Fri · 10:00 – 17:00 EET' },
        { label: 'Incorporated on', value: '13 November 2025' },
        { label: 'Website developer', value: 'KUON · kuon.com.ua', href: 'https://kuon.com.ua', brand: 'kuon' },
      ],
      contactTitle: 'Contact us',
      contactText:
        'Spot inaccurate data, have questions about funding mechanics, or need help with the platform? Write to us and we will reply within one business day.',
      emailCta: 'Email us',
      backButtonLabel: 'Back to dashboard',
    },
    footer: {
      tagline: 'Funding rate analytics for perpetual futures markets.',
      product: 'Product',
      company: 'Company',
      location: 'London, United Kingdom',
      legalName: 'KUON GROUP LTD',
      rights: 'All rights reserved.',
      disclaimer:
        'Information on this website is provided for analytical purposes only and does not constitute investment advice.',
      credit: {
        label: 'Design & development',
        aria: 'Website designed and developed by KUON. Opens kuon.com.ua in a new tab',
        question: 'Like how this site works?',
        answer: 'We built it.',
        text: 'KUON is a digital product studio: websites and apps, marketing and SEO from one team.',
        services: ['Websites', 'Apps', 'UI/UX design', 'Backend & API', 'Marketing', 'SEO'],
        cta: 'Discuss your project',
      },
    },
  },
  uk: {
    header: {
      dashboard: 'Дашборд',
      dailyData: 'Денні дані',
      about: 'Про нас',
      languageLabel: 'Мова',
      brandName: 'FundingPulse',
      navLabel: 'Головна навігація',
      openMenu: 'Відкрити меню',
      closeMenu: 'Закрити меню',
    },
    hero: {
      tag: 'Аналіз у реальному часі',
      title: 'Сигнали ставки фінансування для трейдерів ф’ючерсів',
      text:
        'Дізнавайтеся, коли біржі платять довгим або коротким позиціям, слідкуйте за змінами в часі та тримайте всі важливі ринки в одному чистому робочому просторі.',
      primaryAction: 'Запустити дашборд',
      secondaryAction: 'Про компанію',
    },
    stats: [
      { value: '{exchanges}', label: 'Деривативних бірж у покритті' },
      { value: '2', label: 'Типи контрактів — USDT-M і COIN-M' },
      { value: '365', label: 'Днів історії в одному запиті' },
      { value: 'Щодня', label: 'Автоматичний зріз по всіх біржах' },
    ],
    coverage: {
      label: 'Дані з провідних деривативних бірж',
    },
    market: {
      eyebrow: 'Ринок зараз',
      title: 'Ставки фінансування по біржах',
      lead: 'Середня ставка фінансування безстрокових USDT-M контрактів по біржах — з останнього щоденного зрізу.',
      asset: 'Актив',
      period: 'Період',
      periods: { '1d': '1Д', '30d': '30Д', '90d': '90Д', '365d': '1Р' },
      periodLong: { '1d': 'останній день', '30d': 'останні 30 днів', '90d': 'останні 90 днів', '365d': 'останні 365 днів' },
      highest: 'Найвища',
      lowest: 'Найнижча',
      spread: 'Спред між біржами',
      spreadNote: 'Найвища мінус найнижча',
      average: 'Середня',
      averageNote: 'По {count} біржах',
      snapshot: 'Зріз {date} · USDT-M · {period}',
      cta: 'Відкрити денні дані',
      records: 'Записів',
      noData: 'Для цього активу ще немає зрізу.',
    },
    featuresHeader: {
      eyebrow: 'Платформа',
      title: 'Створено для виваженого аналізу ф’ючерсів',
    },
    features: [
      {
        title: 'Крос-біржевий огляд',
        text: 'Порівнюйте ставки на кількох біржах одночасно, щоб швидше бачити арбітраж.',
      },
      {
        title: 'Гнучкі періоди',
        text: 'Обирайте денні, тижневі або власні проміжки для відстеження динаміки ставок.',
      },
      {
        title: 'Практичні інсайти',
        text: 'Історичний контекст допомагає зрозуміти, куди ймовірно зрушить ставка.',
      },
      {
        title: 'Низька затримка',
        text: 'Надійні фіди з Tier 1 бірж гарантують актуальні дані для ваших моделей.',
      },
    ],
    workspace: {
      eyebrow: 'Дашборд',
      title: 'Аналіз історії ставок фінансування',
      lead: 'Оберіть період, біржу та пару — панель керування лишається під рукою, поки ви гортаєте результати.',
    },
    home: {
      periodTitle: 'Оберіть період',
      exchangeTitle: 'Оберіть біржу',
      contractTypeTitle: 'Тип контракту',
      tradingPairTitle: 'Оберіть торгову пару',
      dock: {
        period: 'Період',
        exchange: 'Біржа',
        pair: 'Пара',
        all: 'Усі',
        hide: 'Згорнути',
        chooseExchangeFirst: 'Спершу оберіть біржу',
        loadingPairs: 'Завантаження пар…',
        noPairs: 'Пар не знайдено',
        emptyTitle: 'Оберіть торгову пару',
        emptyText: 'Тут з’являться статистика, графік ставки фінансування та повна історія.',
      },
      resultTitle: 'Результат аналізу',
      contractType: {
        usdtMDesc: 'USDT-маржинальні безстрокові ф’ючерси',
        coinMDesc: 'Coin-маржинальні безстрокові ф’ючерси',
      },
      periodSelector: {
        dateLocale: 'uk-UA',
        startLabel: 'Дата початку',
        endLabel: 'Дата закінчення',
        startPlaceholder: 'Оберіть дату початку',
        endPlaceholder: 'Оберіть дату закінчення',
        selectedRangeLabel: 'Обраний період',
        periodTypes: [
          { id: 'custom', label: 'Власний період' },
          { id: 'today', label: 'Сьогодні' },
          { id: 'yesterday', label: 'Вчора' },
          { id: 'week', label: '7 днів' },
          { id: 'month', label: '30 днів' },
          { id: 'year', label: '365 днів' },
        ],
      },
      exchangeSelector: {
        searchPlaceholder: 'Пошук біржі',
        searchAria: 'Пошук біржі',
        statusActive: 'Активна',
        statusInactive: 'Неактивна',
        selectedTitle: 'Обрана біржа:',
        selectedSubtitle: 'Готово до вибору торгових пар',
        noResults: 'Немає бірж за запитом «{query}»',
      },
      tradingPairSelector: {
        searchPlaceholder: 'Пошук торгової пари, напр. BTC або USDT',
        searchAria: 'Пошук торгової пари',
        loadingMessage: 'Завантаження торгових пар…',
        errorTitle: 'Помилка',
        errorPrefix: 'Помилка завантаження:',
        placeholderTitle: 'Торгові пари не знайдено',
        placeholderSupportedHint: 'Спробуйте змінити запит або перевірити підключення до біржі.',
        placeholderUnsupportedHint: 'Ця біржа поки що не підтримується.',
        placeholderNoExchangeHint: 'Спочатку виберіть біржу, щоб завантажити торгові пари.',
        paginationPrev: 'Попередня',
        paginationNext: 'Наступна',
        paginationInfo: 'Сторінка {current} з {pages} · {total} пар',
        rateLabel: 'Ставка',
        noCoinM: '{exchange} не має COIN-M контрактів. Оберіть USDT-M, щоб побачити доступні пари.',
        spotOnly: '{exchange} — спотова біржа без безстрокових ф’ючерсів, тому ставки фінансування недоступні.',
      },
      fundingRateResult: {
        loadingMessage: 'Завантаження історичних даних ставки фінансування…',
        noDataTitle: 'Немає даних',
        noDataMessage: 'Не вдалося завантажити дані ставки фінансування',
        analysisTitlePrefix: 'Аналіз ставки фінансування',
        availablePeriodLabel: 'Доступний період даних',
        requestedRangeLabel: 'Запитуваний період:',
        averageLabel: 'Середня ставка',
        totalLabel: 'Сума',
        recordsLabel: 'Кількість записів',
        minLabel: 'Мінімальна ставка',
        maxLabel: 'Максимальна ставка',
        chartTitle: 'Динаміка ставки фінансування',
        detailsTitle: 'Детальні дані за період',
        dateColumn: 'Дата і час',
        rateColumn: 'Ставка',
        sortLabel: 'Сортування',
        sortNewest: 'Спочатку нові',
        sortOldest: 'Спочатку старі',
        dateLocale: 'uk-UA',
      },
    },
    daily: {
      eyebrow: 'Денні дані',
      title: 'Щоденний зріз ставок',
      lead: 'Середні, сумарні, мінімальні та максимальні ставки фінансування по всіх біржах за 1, 30, 90 і 365 днів.',
      dateLocale: 'uk-UA',
      currency: 'Валюта',
      date: 'Дата',
      dateHint: 'Дата автоматично встановлюється залежно від валюти',
      reload: 'Перезавантажити',
      reloading: 'Завантаження…',
      fetchFresh: 'Отримати свіжі дані',
      saving: 'Збереження…',
      availableDates: 'Доступні дати',
      moreDates: 'та ще {count}',
      schedulerTitle: 'Час щоденного оновлення (UTC)',
      schedulerLabel: 'Година : хвилина',
      hour: 'Година',
      minute: 'Хвилина',
      saveTime: 'Зберегти час',
      refresh: 'Оновити',
      loading: 'Завантаження даних…',
      totalExchanges: 'Бірж у зрізі',
      snapshotTime: 'Дата та час зрізу',
      tableTitle: 'Денні ставки фінансування · {currency}',
      exchange: 'Біржа',
      contractType: 'Тип контракту',
      periods: ['1 день', '30 днів', '90 днів', '365 днів'],
      yesterday: 'вчора',
      metrics: { avg: 'Середня', sum: 'Сума', min: 'Мін', max: 'Макс' },
      records: 'записів: {count}',
      chartsTitle: 'Візуалізація даних',
      emptyTitle: 'Немає даних для відображення',
      emptyText: 'Оберіть валюту та натисніть «Перезавантажити», щоб переглянути дані.',
      loaded: 'Дані за {date} для {currency} успішно завантажено',
      notFound: 'Дані за {date} для {currency} не знайдено. Спробуйте іншу дату або зачекайте до 15:00.',
      loadError: 'Помилка завантаження даних. Спробуйте пізніше.',
      savedOk: 'Дані успішно збережено: {file}',
      saveFailed: 'Помилка збереження: {message}',
      saveError: 'Помилка збереження даних. Спробуйте пізніше.',
      invalidTime: 'Невірний час (година 0–23, хвилина 0–59)',
      passwordPrompt: 'Введіть пароль для підтвердження змін',
      cancelled: 'Операцію скасовано: пароль не введено',
      updated: 'Оновлено: {time}',
      serverError: 'Сервер повернув помилку',
      schedulerError: 'Помилка встановлення часу планувальника',
      charts: {
        periods: ['1 день', '30 днів', '90 днів', '365 днів'],
        contractTypes: 'Розподіл типів контрактів',
        average: 'Середні ставки фінансування за періодами',
        max: 'Максимальні ставки фінансування',
        min: 'Мінімальні ставки фінансування',
        total: 'Суми ставок фінансування',
        axisRate: 'Ставка фінансування (%)',
        axisMax: 'Максимальна ставка (%)',
        axisMin: 'Мінімальна ставка (%)',
        axisTotal: 'Сума ставок',
        axisExchanges: 'Біржі',
        records: 'Записів',
      },
    },
    about: {
      eyebrow: 'Компанія',
      title: 'Про нас',
      missionTitle: 'Наша місія',
      missionText:
        'Ми будуємо зручну платформу для аналізу ставок фінансування на найпопулярніших криптобіржах. Наша мета — надати трейдерам та інвесторам швидкий доступ до важливих фінансових даних.',
      fundingTitle: 'Що таке ставка фінансування?',
      fundingText:
        'Ставка фінансування — це періодичний платіж між довгими і короткими позиціями на ринках безстрокових ф’ючерсів. Вона показує різницю між контрактом та спотом: позитивна ставка означає, що довгі платять коротким, і навпаки.',
      platformTitle: 'Переваги платформи',
      platformList: [
        'Моніторинг ставок між біржами в одному вікні.',
        'Гнучкі періоди для поглибленого аналізу.',
        'Аналітичні панелі з візуалізаціями.',
        'Розумні сповіщення про зміни ставок.',
        'Адаптивний інтерфейс для швидкого прийняття рішень.',
      ],
      supportedTitle: 'Підтримувані біржі',
      companyTitle: 'Реквізити компанії',
      infoIntro:
        'Напишіть нам із пропозиціями партнерства, запитами від преси або зворотним зв’язком — нижче найшвидший спосіб зв’язатися.',
      companyInfo: [
        { label: 'Назва компанії', value: 'KUON GROUP LTD' },
        {
          label: 'Адреса офісу',
          value: 'Office 15718 182-184, High Street North, East Ham, London, United Kingdom, E6 2JA',
        },
        { label: 'Телефон', value: '+44 73 5395 8683' },
        { label: 'Електронна пошта', value: 'support@ratex.onmicrosoft.com' },
        { label: 'Години роботи', value: 'Пн – Пт · 10:00 – 17:00 EET' },
        { label: 'Зареєстровано', value: '13 листопада 2025' },
        { label: 'Розробник сайту', value: 'KUON · kuon.com.ua', href: 'https://kuon.com.ua', brand: 'kuon' },
      ],
      contactTitle: 'Зв’язок',
      contactText:
        'Якщо ви помітили неточні дані, маєте питання щодо механіки ставок або потребуєте допомоги — напишіть нам. Ми відповідаємо протягом одного робочого дня.',
      emailCta: 'Написати нам',
      backButtonLabel: 'До дашборду',
    },
    footer: {
      tagline: 'Аналітика ставок фінансування для ринків безстрокових ф’ючерсів.',
      product: 'Продукт',
      company: 'Компанія',
      location: 'Лондон, Велика Британія',
      legalName: 'KUON GROUP LTD',
      rights: 'Усі права захищені.',
      disclaimer:
        'Інформація на сайті надається виключно з аналітичною метою і не є інвестиційною порадою.',
      credit: {
        label: 'Дизайн і розробка',
        aria: 'Сайт спроєктовано й розроблено студією KUON. Відкриває kuon.com.ua у новій вкладці',
        question: 'Сподобалось, як працює цей сайт?',
        answer: 'Це зробили ми.',
        text: 'KUON — студія цифрових продуктів: сайти й застосунки, маркетинг і SEO від однієї команди.',
        services: ['Сайти', 'Застосунки', 'UI/UX дизайн', 'Бекенд і API', 'Маркетинг', 'SEO'],
        cta: 'Обговорити проєкт',
      },
    },
  },
};

const readStoredLanguage = () => {
  try {
    const stored = window.localStorage.getItem('fp.language');
    return stored === 'uk' || stored === 'en' ? stored : 'en';
  } catch (e) {
    return 'en';
  }
};

function App() {
  const [language, setLanguage] = useState(readStoredLanguage);
  const locale = translations[language];
  const contactEmail = locale.about.companyInfo.find((item) => item.value.includes('@'))?.value;

  useEffect(() => {
    document.documentElement.lang = language;
    try {
      window.localStorage.setItem('fp.language', language);
    } catch (e) {
      // storage can be unavailable (private mode); the choice just won't persist
    }
  }, [language]);

  return (
    <Router>
      <ScrollToTop />
      <SiteHeader locale={locale.header} language={language} onLanguageChange={setLanguage} />
      <Main>
        <AnimatedRoutes locale={locale} />
      </Main>
      <DeveloperCredit locale={locale.footer.credit} />
      <SiteFooter locale={locale.footer} header={locale.header} email={contactEmail} />
    </Router>
  );
}

export default App;
